import React from "react";
import {observer} from "mobx-react";
import {useInjection} from "inversify-react";
import {AppController} from "@dropDesk/presentation/app/app.controller";
import DetailSubscription from "@dropDesk/presentation/components/detail_subscription";
import {PaymentSubscriptionEntity} from "@dropDesk/domain/entities/payment_subscriptions/payment_subscription_entity";
import {CompanyExtensionEntity} from "@dropDesk/domain/entities/plan_extensions/company_extension.entity";
import {UserEntity} from "@dropDesk/domain/entities/user/user.entity";
import SubscriptionOverviewRow from "./row";
import {
    TableWrapper,
    OverviewTable,
    TableHead,
    TableBody,
    TableHeaderRow,
    TableHeaderCell,
    StatusBadge,
    ContentButton,
} from "./styled";
import {Kind} from "@dropDesk/domain/entities/payment_subscriptions/payment_subscription.enum";
import {CompanyExtensionStatusEnum} from "@dropDesk/domain/entities/plan_extensions/plan_extension.enum";
import {ActionButton} from "./styled";
import {useBetween} from "use-between";
import {useModalsSubscription} from "@dropDesk/presentation/pages/subscription/ui/modals/visible_modal_subscription";
import {ICONS_DROPDESK} from "@dropDesk/storage/icons/icons.d";
import ButtonMain from "@dropDesk/presentation/components/buttons/button_main";
import {SharedColors} from "@dropDesk/domain/entities/theme/app_colors";
import {formatCurrency} from "@dropDesk/utils/helpers/common";

export type OverviewRow =
    | { kind: Kind.plan; data: PaymentSubscriptionEntity; isCurrent: boolean }
    | { kind: Kind.extension; data: CompanyExtensionEntity };

const SubscriptionOverviewTable = observer((
    {
        currentSubscription,
        oldSubs,
        hasUpgrade,
        hasDowngrade,
        extensions,
        loading,
        disabled,
        currentUser,
        onClickSelectPlan,
        onClickReactivePlan,
        onClickChangePlan,
        onClickCancelFutureSubscription,
        visibleButtonChangePlan,
        visibleCancelButton,
    }: {
        currentSubscription: PaymentSubscriptionEntity;
        oldSubs?: PaymentSubscriptionEntity;
        hasUpgrade: boolean;
        hasDowngrade: boolean;
        extensions?: CompanyExtensionEntity[];
        loading: boolean;
        disabled: boolean;
        currentUser?: UserEntity | null;
        onClickSelectPlan: () => void;
        onClickReactivePlan: () => void;
        onClickChangePlan: () => void;
        onClickCancelFutureSubscription: () => void;
        visibleButtonChangePlan: boolean;
        visibleCancelButton: boolean;
    }) => {
    const appController = useInjection(AppController);
    const colors = appController.theme.colorScheme;
    const {setVisibleModalCancelExtension, setVisibleModalReactivateExtension} = useBetween(useModalsSubscription);

    const rows: OverviewRow[] = [
        {kind: Kind.plan, data: currentSubscription, isCurrent: true},
        ...(oldSubs && (hasUpgrade || hasDowngrade)
            ? [{kind: Kind.plan as Kind.plan, data: oldSubs, isCurrent: false}]
            : []),
        ...(extensions ?? []).map((ext): OverviewRow => ({kind: Kind.extension, data: ext})),
    ];

    const nextDueDateLabel = currentSubscription.isCanceledScheduled(currentUser)
        ? 'Vigência até'
        : 'Próximo vencimento';

    const COLUMNS = ['Produto', 'Descrição', 'Status', nextDueDateLabel, 'Valor', 'Ações'];

    const subscriptionCanceledScheduled = currentSubscription.isCanceledScheduled(currentUser);

    const getStatusAndButton = (row: OverviewRow): { status: React.ReactNode; button: React.ReactNode } => {
        if (row.kind === Kind.extension) {
            const ext = row.data as CompanyExtensionEntity;
            const buttonCancel = (
                <ContentButton style={{width: 180}}>
                    <ButtonMain
                        style={{paddingLeft: 5, paddingRight: 5}}
                        widthPercentage={100}
                        letterFontSize={12}
                        disabled={disabled}
                        loading={loading}
                        height={35}
                        color={'transparent'}
                        letterColor={colors.errorOnPrimary}
                        borderColor={colors.errorOnPrimary}
                        type={"submit"}
                        letter={'Cancelar extensão'}
                        useSuffixIcon={true}
                        onClick={() => setVisibleModalCancelExtension({visible: true, extension: ext})}
                    />
                </ContentButton>
            )
            if (ext.isActive) {
                return {
                    status: ext.isFree ? (
                        <div style={{display: 'flex', gap: 4, flexWrap: 'wrap'}}>
                            <StatusBadge background={`${colors.success}20`} color={colors.success}>
                                Ativo
                            </StatusBadge>
                            <StatusBadge background={`${colors.accent}20`} color={colors.accent}>
                                Grátis
                            </StatusBadge>
                        </div>
                    ) : (
                        <StatusBadge background={`${colors.success}20`} color={colors.success}>
                            Ativo
                        </StatusBadge>
                    ),
                    button: !subscriptionCanceledScheduled && ext.isCancelable ? buttonCancel : null,
                };
            }
            if (ext.isScheduledCancel) {
                return {
                    status: (
                        <StatusBadge background={`${colors.errorOnPrimary}`} color={SharedColors.white}>
                            Cancelamento agendado
                        </StatusBadge>
                    ),
                    button: !subscriptionCanceledScheduled ? (
                        <ContentButton style={{width: 140}}>
                            <ButtonMain
                                style={{paddingLeft: 5, paddingRight: 5}}
                                widthPercentage={100}
                                letterFontSize={14}
                                disabled={disabled}
                                loading={loading}
                                height={35}
                                color={colors.accent}
                                type={"submit"}
                                letter={'Reativar'}
                                useSuffixIcon={true}
                                onClick={() => setVisibleModalReactivateExtension({visible: true, extension: ext})}
                            />
                        </ContentButton>
                    ) : null,
                };
            }
            if (ext.isWaitingPayment) {
                return {
                    status: (
                        <StatusBadge background={SharedColors.onAttendingStatus}
                                     color={SharedColors.textAttendingStatus}>
                            Aguardando pagamento
                        </StatusBadge>
                    ),
                    button: !subscriptionCanceledScheduled ? buttonCancel : null,
                };
            }
            if (ext.isPaymentRequired) {
                return {
                    status: (
                        <StatusBadge background={SharedColors.onAttendingStatus}
                                     color={SharedColors.textAttendingStatus}>
                            Pagamento necessário
                        </StatusBadge>
                    ),
                    button: !subscriptionCanceledScheduled ? buttonCancel : null,
                };
            }
            return {status: null, button: null};
        }

        const subscription = row.data as PaymentSubscriptionEntity;
        const activeStatusBadge = (
            <StatusBadge background={`${colors.success}20`} color={colors.success}>
                Ativo
            </StatusBadge>
        );
        if (!row.isCurrent) {
            return {
                status: activeStatusBadge,
                button: null,
            };
        }


        const buttonSelectPlan = (
            <ContentButton style={{width: 160}}>
                <ButtonMain
                    style={{paddingLeft: 10, paddingRight: 5}}
                    widthPercentage={100}
                    letterFontSize={14}
                    disabled={disabled}
                    loading={loading}
                    color={colors.accent}
                    height={35}
                    useSuffixIcon={true}
                    type={"submit"}
                    letter={'Assinar agora'}
                    onClick={() => onClickSelectPlan()}
                />
            </ContentButton>
        );

        if (subscription.isFreeActiveSubscription) {
            return {
                status: (
                    <div style={{display: 'flex', gap: 4, flexWrap: 'wrap'}}>
                        {activeStatusBadge}
                        <StatusBadge background={`${colors.accent}20`} color={colors.accent}>
                            Grátis
                        </StatusBadge>
                    </div>
                ),
                button: buttonSelectPlan,
            };
        }

        if (subscription.isCanceledScheduled(currentUser)) {
            return {
                status: (
                    <StatusBadge background={`${colors.errorOnPrimary}`} color={SharedColors.white}>
                        Cancelamento agendado
                    </StatusBadge>
                ),
                button: (
                    <ContentButton style={{width: 200}}>
                        <ButtonMain
                            style={{paddingLeft: 5, paddingRight: 5}}
                            widthPercentage={100}
                            height={35}
                            letterFontSize={14}
                            disabled={disabled}
                            loading={loading}
                            color={colors.accent}
                            type={"submit"}
                            letter={'Reativar assinatura'}
                            useSuffixIcon={true}
                            onClick={() => onClickReactivePlan()}
                        />
                    </ContentButton>
                ),
            };
        }

        if (subscription.isExpired()) {
            return {
                status: (
                    <StatusBadge background={`${colors.errorOnPrimary}`} color={SharedColors.white}>
                        Expirado
                    </StatusBadge>
                ),
                button: buttonSelectPlan,
            };
        }

        if (subscription.isWaitingPayment && hasUpgrade && !oldSubs?.isFree) {
            return {
                status: (
                    <div style={{display: 'flex', gap: 4, flexWrap: 'wrap'}}>
                        <StatusBadge background={`${colors.success}20`} color={colors.success}>
                            Upgrade
                        </StatusBadge>
                        <StatusBadge
                            background={SharedColors.onAttendingStatus}
                            color={SharedColors.textAttendingStatus}
                        >
                            Aguardando pagamento
                        </StatusBadge>
                    </div>
                ),
                button: visibleCancelButton ? (
                    <ContentButton style={{width: 250}}>
                        <ButtonMain
                            style={{paddingLeft: 5, paddingRight: 5}}
                            widthPercentage={100}
                            letterFontSize={14}
                            disabled={disabled}
                            loading={loading}
                            height={35}
                            color={colors.accent}
                            type={"submit"}
                            letter={'Desfazer troca de plano'}
                            useSuffixIcon={true}
                            onClick={() => onClickCancelFutureSubscription()}
                        />
                    </ContentButton>
                ) : null,
            };
        }

        if (subscription.isWaitingPayment) {
            return {
                status: (
                    <div style={{display: 'flex', gap: 4, flexWrap: 'wrap'}}>
                        <StatusBadge background={`${colors.success}20`} color={colors.success}>
                            Ativo
                        </StatusBadge>
                        <StatusBadge
                            background={SharedColors.onAttendingStatus}
                            color={SharedColors.textAttendingStatus}
                        >
                            Aguardando pagamento
                        </StatusBadge>
                    </div>
                ),
                button: null,
            };
        }

        if (hasDowngrade) {
            return {
                status: (
                    <StatusBadge
                        background={SharedColors.onAttendingStatus}
                        color={SharedColors.textAttendingStatus}
                    >
                        Downgrade agendado
                    </StatusBadge>
                ),
                button: visibleCancelButton ? (
                    <ContentButton style={{width: 250}}>
                        <ButtonMain
                            style={{paddingLeft: 5, paddingRight: 5}}
                            widthPercentage={100}
                            letterFontSize={14}
                            disabled={disabled}
                            loading={loading}
                            height={35}
                            color={colors.accent}
                            type={"submit"}
                            letter={'Desfazer troca de plano'}
                            useSuffixIcon={true}
                            onClick={() => onClickCancelFutureSubscription()}
                        />
                    </ContentButton>
                ) : null,
            };
        }

        return {
            status: activeStatusBadge,
            button: visibleButtonChangePlan ? (
                <ContentButton style={{width: 160}}>
                    <ButtonMain
                        style={{paddingLeft: 5, paddingRight: 5}}
                        widthPercentage={100}
                        letterFontSize={14}
                        disabled={disabled}
                        loading={loading}
                        height={35}
                        color={colors.accent}
                        type={"submit"}
                        letter={'Trocar plano'}
                        useSuffixIcon={true}
                        onClick={() => onClickChangePlan()}
                    />
                </ContentButton>
            ) : null,
        };
    };

    const getType = (row: OverviewRow): string => {
        if (row.kind === Kind.extension) {
            return "Extensão";
        }
        return "Plano";
    };

    const getIconType = (row: OverviewRow): string => {
        if (row.kind === Kind.extension) {
            return ICONS_DROPDESK.extension;
        }
        return ICONS_DROPDESK.subscription;
    };

    const getProductName = (row: OverviewRow): string => {
        if (row.kind === Kind.extension) {
            return (row.data as CompanyExtensionEntity).extensionCatalog?.name ?? '';
        }
        return (row.data as PaymentSubscriptionEntity).planName;
    };

    const getProductDescriptionColor = (row: OverviewRow): string | undefined => {
        if (row.kind === Kind.extension) {
            const extension = (row.data as CompanyExtensionEntity);
            if (extension.isFreeNotCore && !extension.isSourceLegacy) {
                return colors.textError;
            }
            return colors.hint;
        }
        if (row.kind === Kind.plan && row.isCurrent && (row.data as PaymentSubscriptionEntity).isFreeActiveSubscription) {
            return colors.textError;
        }
        return undefined;
    };

    const getProductDescription = (row: OverviewRow): string => {
        if (row.kind === Kind.extension) {
            const extension = row.data as CompanyExtensionEntity;
            if (extension.isFreeFromAccountMigrated && !extension.extensionCatalog?.isCore && !extension.isSourceLegacy) {
                const days = currentSubscription.isFreeActiveSubscription
                    ? currentSubscription.daysToExpireSubs(oldSubs)
                    : extension.daysToExpiration;
                return `Extensão vinculada à assinatura e termina em ${days} dia${days !== 1 ? 's' : ''}`;
            }
            return 'Extensão vinculada à assinatura';
        }
        return (row.data as PaymentSubscriptionEntity).descriptionUsersPlan;
    };

    const getNextDueDate = (row: OverviewRow): string => {
        if (row.kind === Kind.extension) {
            const ext = row.data as CompanyExtensionEntity;
            if (ext.isFreeFromAccountMigrated) {
                return '—';
            }
            if (ext.isScheduledCancel) {
                return `Cancela em ${ext.cancelScheduledToDDMMYYYY ?? ext.nextDueDateDDMMYYYY}`;
            }
            return ext.nextDueDateDDMMYYYY;
        }
        const subscription = row.data as PaymentSubscriptionEntity;
        if (!row.isCurrent) {
            return `Ativo até ${subscription.nextDueDateDDMMYYYY}`;
        }
        if (subscription.isCanceledScheduled(currentUser)) {
            return `Acesso até ${subscription.nextDueDateDDMMYYYY}`;
        }
        if (subscription.isExpired()) {
            return `Encerrada em ${subscription.nextDueDateDDMMYYYY}`;
        }
        if (subscription.isWaitingPayment) {
            return `Pgto. expira em ${subscription.lastPaymentExpireDateDDMMYYYY ?? subscription.nextChargeDateDDMMYYYY}`;
        }
        if (hasDowngrade) {
            return `Entra em vigor em ${subscription.nextDueDateDDMMYYYY}`;
        }
        return subscription.nextDueDateDDMMYYYY;
    };

    const isWaitingPaymentRow = (row: OverviewRow): boolean => {
        return row.kind === Kind.plan
            && row.isCurrent
            && (row.data as PaymentSubscriptionEntity).isWaitingPayment
            && !!(row.data as PaymentSubscriptionEntity).lastPayment?.value;
    };


    const getValueNote = (row: OverviewRow): string | undefined => {
        if (!isWaitingPaymentRow(row)) {
            return undefined;
        }
        return `Valor mensal: ${formatCurrency((row.data as PaymentSubscriptionEntity).totalValue)}`;
    };

    const getValueText = (row: OverviewRow): string | undefined => {
        if (row.kind === Kind.extension && (row.data as CompanyExtensionEntity).isFreeFromAccountMigrated) {
            return '—';
        }
        return undefined;
    };

    const getValue = (row: OverviewRow): number => {
        if (row.kind === Kind.extension) {
            return (row.data as CompanyExtensionEntity).unitPrice;
        }
        const subscription = row.data as PaymentSubscriptionEntity;
        if (isWaitingPaymentRow(row)) {
            return subscription.lastPayment?.value ?? subscription.totalValue;
        }
        return subscription.totalValue;
    };

    return (
        <DetailSubscription
            title={'Visão geral da assinatura'}
            description={'Plano e extensões ativos na sua conta.'}
            content={
                <TableWrapper borderColor={'transparent'}>
                    <OverviewTable>
                        <TableHead>
                            <TableHeaderRow
                                background={'transparent'}
                                borderColor={colors.border}
                            >
                                {COLUMNS.map(col => (
                                    <TableHeaderCell key={col} color={colors.hintInverse}>
                                        {col}
                                    </TableHeaderCell>
                                ))}
                            </TableHeaderRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row, index) => (
                                <SubscriptionOverviewRow
                                    key={index}
                                    statusAndButton={getStatusAndButton(row)}
                                    type={getType(row)}
                                    iconType={getIconType(row)}
                                    productName={getProductName(row)}
                                    productDescription={getProductDescription(row)}
                                    productDescriptionColor={getProductDescriptionColor(row)}
                                    nextDueDate={getNextDueDate(row)}
                                    value={getValue(row)}
                                    valueText={getValueText(row)}
                                    valueNote={getValueNote(row)}
                                />
                            ))}
                        </TableBody>
                    </OverviewTable>
                </TableWrapper>
            }
        />
    );
});

export default SubscriptionOverviewTable;
